import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { CodeBracketIcon } from "@heroicons/react/24/outline";
import {
  createMeetingMinutesTemplate,
  fetchMeetingMinutesTemplate,
  updateMeetingMinutesTemplate,
} from "../../../../api/meetingMinutes";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { AIGenerator } from "../../../../components/utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../../config/aiGeneratorConfigs";
import { MeetingMinutesJsonDialog } from "../../../../components/dashboard/meeting-minutes/MeetingMinutesJsonDialog";
import {
  applyParsedAgenda,
  parseImportedMeetingMinutesJson,
} from "../../../../components/dashboard/meeting-minutes/meetingMinutesImportUtils";
import {
  MeetingMinutesShell,
  mmCardClass,
  mmPrimaryButtonClass,
  mmSecondaryButtonClass,
} from "../MeetingMinutesShell";
import { MeetingMinutesTypePills } from "../MeetingMinutesTypePills";
import { MeetingMinutesAgendaEditor, emptyGroup } from "../MeetingMinutesAgendaEditor";

export const MeetingMinutesTemplateEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    meeting_type: "project_meeting",
    active: true,
  });
  const [groups, setGroups] = useState([emptyGroup(0)]);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  const applyAgenda = (parsed, mode = "replace") => {
    setGroups((prev) => applyParsedAgenda(parsed, prev, mode));
    if (parsed.title) {
      setFormData((prev) => ({ ...prev, title: parsed.title }));
    }
    if (parsed.description) {
      setFormData((prev) => ({ ...prev, description: parsed.description }));
    }
    if (parsed.meeting_type) {
      setFormData((prev) => ({ ...prev, meeting_type: parsed.meeting_type }));
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchMeetingMinutesTemplate(id);
        setFormData({
          title: data.title || "",
          description: data.description || "",
          meeting_type: data.meeting_type || "project_meeting",
          active: data.active !== false,
        });
        setGroups(
          (data.groups || []).map((group, index) => ({
            ...group,
            key: `g-${group.id || index}`,
            entries: (group.entries || []).map((entry, entryIndex) => ({
              ...entry,
              key: `e-${entry.id || entryIndex}`,
            })),
          }))
        );
      } catch (err) {
        toast.error(t("meeting_minutes.template.load_error"));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        groups: groups.map((group, index) => ({
          title: group.title,
          description: group.description,
          order: index,
          entries: (group.entries || [])
            .filter((entry) => (entry.description || "").replace(/<[^>]*>/g, "").trim())
            .map((entry, entryIndex) => ({
              description: entry.description,
              deadline: entry.deadline,
              order: entryIndex,
            })),
        })),
      };
      if (isEdit) {
        const result = await updateMeetingMinutesTemplate({ ...payload, id });
        if (result?.errors) throw new Error(result.errors.join?.(", "));
        toast.success(t("meeting_minutes.template.updated"));
      } else {
        const result = await createMeetingMinutesTemplate(payload);
        if (result?.errors) throw new Error(result.errors.join?.(", "));
        toast.success(t("meeting_minutes.template.created"));
      }
      navigate("/dashboard/meeting-minutes/templates");
    } catch (err) {
      toast.error(
        isEdit
          ? t("meeting_minutes.template.update_error")
          : t("meeting_minutes.template.create_error")
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ResourceLoading />;

  return (
    <MeetingMinutesShell
      title={isEdit ? t("meeting_minutes.templates.edit") : t("meeting_minutes.templates.new")}
      subtitle={t("meeting_minutes.templates.editor_subtitle")}
      backTo="/dashboard/meeting-minutes/templates"
      backLabel={t("Back")}
    >
      <form onSubmit={handleSubmit} className={`${mmCardClass()} space-y-6 px-6 py-6`}>
        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            {t("meeting_minutes.title")} *
          </label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            {t("meeting_minutes.meeting_type")}
          </label>
          <MeetingMinutesTypePills
            value={formData.meeting_type}
            onChange={(meeting_type) => setFormData({ ...formData, meeting_type })}
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            {t("meeting_minutes.description")}
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-[#1463C7]"
          />
          {t("active")}
        </label>

        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              {t("meeting_minutes.templates.agenda")}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setJsonDialogOpen(true)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                <CodeBracketIcon className="h-4 w-4" />
                {t("meeting_minutes.json.open_dialog")}
              </button>
              <AIGenerator
                module="meeting_minutes"
                context={{
                  minutes_title: formData.title || "",
                  minutes_description: formData.description || "",
                  meeting_type: formData.meeting_type || "",
                }}
                fieldsToGenerate={["agenda_json"]}
                onGenerate={(generatedFields, meta) => {
                  const raw = generatedFields?.agenda_json;
                  if (raw == null || typeof raw !== "string" || !String(raw).trim()) {
                    toast.error(t("meeting_minutes.json.ai_parse_error"));
                    return;
                  }
                  try {
                    const parsed = parseImportedMeetingMinutesJson(raw);
                    const applyMode = meta?.generationOptions?.apply_mode || "replace";
                    applyAgenda(parsed, applyMode);
                    toast.success(
                      t("meeting_minutes.json.ai_apply_success", {
                        count: parsed.groups.length,
                      })
                    );
                  } catch (err) {
                    const code = err?.code || "unknown";
                    const key = `meeting_minutes.json.error_${code}`;
                    const msg = t(key);
                    toast.error(
                      msg !== key ? msg : t("meeting_minutes.json.ai_parse_error")
                    );
                  }
                }}
                config={aiGeneratorConfigs.meeting_minutes}
                buttonLabel={t("meeting_minutes.json.generate_with_ai")}
                className="!rounded-full !border-slate-300 !px-4 !py-2 !text-sm !font-semibold"
              />
            </div>
          </div>
          <MeetingMinutesAgendaEditor groups={groups} onChange={setGroups} />
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-100 pt-6">
          <button
            type="button"
            onClick={() => navigate("/dashboard/meeting-minutes/templates")}
            className={mmSecondaryButtonClass()}
          >
            {t("Cancel")}
          </button>
          <button type="submit" disabled={saving} className={mmPrimaryButtonClass()}>
            {saving ? t("Saving...") : isEdit ? t("Update") : t("Create")}
          </button>
        </div>
      </form>
      <MeetingMinutesJsonDialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        title={formData.title}
        description={formData.description}
        meeting_type={formData.meeting_type}
        groups={groups}
        onApply={(parsed) => applyAgenda(parsed, parsed.mode || "replace")}
      />
    </MeetingMinutesShell>
  );
};
